/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import PaginatedListState from 'flarum/common/states/PaginatedListState';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import reviewExportState from '../utils/reviewExportState';
import app from 'flarum/forum/app';
import DiscussionListState, { type DiscussionListParams } from 'flarum/forum/states/DiscussionListState';
import Discussion from 'flarum/common/models/Discussion';
import type Article from 'flarum/orell-articles/models/Article';
import type { ReviewListRequestParams } from '../types';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;
import ConfirmModal from '../components/ConfirmModal';
import { ApiResponsePlural } from 'flarum/common/Store';

/** Google Analytics payload passed to `Auth.authenticatedCall`. */
export interface GaExportAttrs {
	hitType: string;
	eventCategory: string;
	eventAction: string;
	eventLabel: string;
	[field: string]: unknown;
}

export default class ReviewListState extends DiscussionListState {
	/** Only set when the list belongs to an article page. */
	article?: Article;

	/** Discussion id → whether its review is currently being exported. */
	exporting: Record<string, boolean>;

	/** Export report counters — `delete`d again by `hideExportedReport()`. */
	toExportCount?: number;
	successCount?: number;
	failedCount?: number;

	constructor(params: DiscussionListParams = {}, page?: number, article?: Article) {
		if (article) {
			super(params, page); // Required, then redone below with a page size of 5.
			Reflect.apply(PaginatedListState.prototype.constructor, this, [params, page, 5]);
			this.article = article;
		} else {
			super(params, page);
		}

		this.exporting = {};
	}

	get filterKeys(): string[] {
		return ['all', 'exported', 'not_exported'];
	}

	requestParams(): ReviewListRequestParams {
		const params = super.requestParams() as ReviewListRequestParams;

		params.filter = { ...this.params.filter, ...params.filter };

		delete params.filter['-isReview'];
		params.filter['isReview'] = true;

		const urlParams = m.route.param();

		if (urlParams.filter === 'exported') {
			params.filter['exported'] = true;
		} else if (urlParams.filter === 'not_exported') {
			params.filter['-exported'] = true;
		}

		if (this.article) {
			params.filter.article = this.article.id();
		}

		return params;
	}

	sortMap(): Record<string, string> {
		const map = super.sortMap();
		delete map.front;
		return map;
	}

	/** CORE_CODE_OVERRIDE: `DiscussionListState.loadPage`, with the preloaded document vetted. */
	protected loadPage(page: number = 1): Promise<ApiResponsePlural<Discussion>> {
		const preloadedDiscussions = app.preloadedApiDocument<Discussion[]>();

		if (Array.isArray(preloadedDiscussions) && preloadedDiscussions.every((item) => item instanceof Discussion)) {
			this.initialLoading = false;

			return Promise.resolve(preloadedDiscussions);
		}

		return super.loadPage(page);
	}

	/**
	 * Returns nothing on the early-out path, where the base always returns a promise. Typed `any`
	 * because `void` is not assignable to `Promise<void>`, and no caller reads the return value.
	 */
	refresh(): any {
		// An article page with no reviews has nothing to fetch. The main list always has.
		if (this.article && !this.article.reviewCount()) {
			this.initialLoading = false;
			this.loadingPrev = false;
			this.loadingNext = false;
		} else {
			super.refresh();
		}
	}

	changeFilter(filter: string): void {
		const params = { ...m.route.param() };

		if (filter === this.filterKeys[0]) {
			delete params.filter;
		} else {
			params.filter = filter;
		}

		delete params.key;

		setRouteWithForcedRefresh(app.route(app.current.get('routeName'), params));
	}

	get exportingInProgress(): boolean {
		return Object.values(this.exporting).some((v) => v);
	}

	hideExportedReport(): void {
		delete this.toExportCount;
		delete this.successCount;
		delete this.failedCount;
		m.redraw();
	}

	async selectAll(selected: boolean): Promise<void> {
		this.hideExportedReport();

		if (selected) {
			if (this.hasNext()) {
				// The user may not expect selecting all to reach beyond the loaded page.
				app.alerts.show({ type: 'success' }, app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.select_all_warning'));
			}

			// Nothing can be selected before it is loaded.
			while (this.hasNext()) {
				await this.loadNext();
			}
		}

		this.getAllItems().forEach((discussion) => {
			if (!reviewExportState(discussion).selectable) {
				return;
			}

			discussion.pushAttributes({ selectedForExport: selected });
		});
	}

	get selectedCount(): number {
		return this.getAllItems().filter((discussion) => discussion.selectedForExport()).length;
	}

	get exportedCount(): number {
		return this.getAllItems().filter((discussion) => reviewExportState(discussion).countsAsExported).length;
	}

	get unavailableCount(): number {
		return this.getAllItems().filter((discussion) => reviewExportState(discussion).unavailable).length;
	}

	get lockedCount(): number {
		return this.getAllItems().filter((discussion) => reviewExportState(discussion).locked).length;
	}

	get unapprovedCount(): number {
		return this.getAllItems().filter((discussion) => reviewExportState(discussion).notApproved).length;
	}

	get hiddenCount(): number {
		return this.getAllItems().filter((discussion) => discussion.isHidden()).length;
	}

	get allExported(): boolean {
		return this.getAllItems().every((discussion) => {
			const { exported, blocked } = reviewExportState(discussion);

			return exported || blocked;
		});
	}

	get selectableCount(): number {
		return this.getAllItems().filter((discussion) => reviewExportState(discussion).selectable).length;
	}

	get allSelected(): boolean {
		return this.selectedCount === this.selectableCount;
	}

	/** Exports `discussion` alone, on a state that has nothing loaded. */
	exportOnly(discussion: Discussion): Promise<void> {
		this.pages = [{ number: 1, items: [discussion] }];

		return this.export();
	}

	async export(): Promise<void> {
		this.hideExportedReport();

		if (!this.selectedCount) {
			return;
		}

		if (!app.session.user) {
			return;
		}

		const toExport = this.getAllItems().filter((discussion) => discussion.selectedForExport());

		if (
			!(await new Promise((resolve) => {
				app.modal.show(ConfirmModal, {
					message: app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_confirm', { count: toExport.length }),
					resolve,
				});
			}))
		) {
			return;
		}

		this.hideExportedReport();

		this.toExportCount = toExport.length;
		this.successCount = 0;
		this.failedCount = 0;

		for (const discussion of toExport) {
			this.exporting[discussion.id()!] = true;
			m.redraw();

			const exportCb = async (discussion: Discussion) => {
				try {
					const review = discussion.review();

					if (!review) {
						throw new Error(`Discussion ${discussion.id()} carries no review to export.`);
					}

					await review.save({ exported: true });
					discussion.pushAttributes({ selectedForExport: false });
					this.successCount!++;
				} catch (error) {
					if ((error as { status?: number }).status === 401) {
						throw error; // Re-authentication is Auth.authenticatedCall's job.
					} else {
						// Refetch, mainly to pick up an article that is no longer available.
						await app.store.find('discussions', discussion.id()!);
						m.redraw();

						this.failedCount!++;
					}
				}
			};

			let linkArgs: unknown[] = [];
			if (!app.session.user.linkedShop()) {
				linkArgs = [app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_connect'), ReviewListState.gaExportAttrs];
			}

			try {
				// orell-thalia-common's generated typings declare the optional parameters as
				// `null`, so widen them locally.
				await (Auth.authenticatedCall as (...args: unknown[]) => Promise<unknown>)(() => exportCb(discussion), ...linkArgs);
			} catch (error) {
				// Most likely the user interrupted the run; reset so they can start over.
				this.toExportCount = undefined;
				this.successCount = 0;
				this.failedCount = 0;
				this.exporting = {};
				m.redraw();
				return;
			}

			this.exporting[discussion.id()!] = false;
			m.redraw();
		}

		this.failedCount = toExport.length - this.successCount!;

		app.alerts.show(
			{
				type: !!this.successCount ? 'success' : undefined,
				className: 'ReviewsExportSummaryAlert',
			},
			<div className="ReviewsExportSummaryAlert-body">
				{!!this.successCount && (
					<div className="exportSuccess">
						{app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_success', {
							success: this.successCount,
							total: this.toExportCount,
						})}
					</div>
				)}

				{!!this.failedCount && (
					<div className="exportFailed">
						{app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_failed', {
							failed: this.failedCount,
						})}
					</div>
				)}
			</div>
		);
	}

	static get gaExportAttrs(): GaExportAttrs {
		return {
			hitType: 'event',
			eventCategory: 'exportReviews',
			eventAction: 'linkOFAccount',
			eventLabel: 'Successful linking of OF Account while trying to export reviews to the OF shop',
		};
	}
}
